import { useCallback, useEffect, useMemo } from 'react';
import { makeLinkPreviewReadyHandler } from '../../components/common/chat/timeline/linkPreviewRealtime';
import { buildChatDropPlan, describeChatDropResult } from '../../components/common/chat/composer/chatDropPlan';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useAdminEventChatRuntime(context) {
  const {
    ADMIN_MESSAGE_FORMATS,
    ADMIN_PROFILE_IMAGE_PATH,
    ARTIST_PROFILE_IMAGE_PATH,
    CHAT_MODE_GROUP_UNIFIED,
    SOCKET_EVENTS,
    STALE_MS,
    adminLookup,
    appendVariant,
    bannerTimeoutRef,
    buildAdminProfilePayload,
    buildArtistProfilePayload,
    buildAssetUrl,
    buildParticipantEventMembershipLabel,
    buildParticipantEventPartSummary,
    buildRealtimeEventTextMessage,
    bumpEventToTop,
    chatContainerRef,
    cleanupTimeoutRefs,
    clearPendingEventRead,
    closeImgModal,
    confirmAssignmentForEvents,
    createEventAvatarProfileTargetResolver,
    createEventAvatarRenderers,
    effectiveGroupEvents,
    eventChatAreaRef,
    events,
    fetchMessages,
    fetchMessagesRef,
    fetchNewMessages,
    fetchNewMessagesRef,
    fetchSidebarRow,
    findPartLabelByIdRef,
    formatAdminDisplayName,
    getAdminEventChatNotifyMute,
    getAnnouncementIconClass,
    getAnnouncementToneClass,
    getEventDetail,
    handleSelectEvent,
    hasMore,
    hasNewerMessages,
    imgModalOpen,
    isAnnouncementMessage,
    isEventChatAreaOpenRef,
    isFetching,
    isMyAdminMessage,
    isNarrow,
    isNearTopForPaging,
    isNoteDeleteMessage,
    isNoteMessage,
    isNoteUpdateMessage,
    isSavingSettings,
    isSearchModeRef,
    isSelectedGroupUnified,
    isViewingLatest,
    lastDiffFetchRef,
    loadNewerMessages,
    loadingNewerMessages,
    loggedInAdminId,
    markEventUnreadLocally,
    markLatestRead,
    mergeMessages,
    messageIdsRef,
    messages,
    messagesRef,
    mobileEventChatHistoryRef,
    mobileEventListPrefRef,
    muteSetting,
    normalizeEventUuidList,
    nowJstDbString,
    offset,
    openBanner,
    openProfileTarget,
    orderFrozenRef,
    parsePartIds,
    participantArtistMap,
    participantSnapshot,
    patchMessageReadCountsByIds,
    pickFirstString,
    postEventReadReceipt,
    reconcileRecentMessages,
    reconcileTimerRef,
    refreshAnnouncements,
    refreshEventChatUnread,
    refreshMessageReactionDetails,
    refreshPendingMentionTargets,
    refreshTopTimerRef,
    removeMessages,
    replyDraft,
    requestRefreshTop,
    resolveEventChatMode,
    resolveEventGroupCount,
    resolveThreadIdByEvent,
    selectedEventId,
    selectedEventIdRef,
    selectedEventInfo,
    selectedThreadIdRef,
    selfReadGuardRef,
    sendEventChatFileMessage,
    sendImages,
    setEvents,
    setIsLoadingSettings,
    setIsSavingSettings,
    setIsUploading,
    setMessages,
    setMobileView,
    setMuteSetting,
    setOffset,
    setReplyDraft,
    setSelectedEventInfo,
    setSettingsEvent,
    setShowNewMsgBanner,
    setShowNotes,
    setShowSettingsModal,
    setSidebarDetails,
    settingsEventIdRef,
    settingsEventRef,
    shouldExcludeAdminSenderForMessage,
    shouldMarkLatestAsRead,
    shouldShowEventMessageSourceBadge,
    showNotes,
    showNotesRef,
    sidebarPressRef,
    socket,
    success,
    toastError,
    updateAdminEventChatNotifyMute,
    updateSidebarAfterSend,
    upsertEventRow,
    useChatDragDrop,
  } = context;
    /* スクロール監視（ページング・既読） */
    const handleScroll = useCallback(() => {
      const c = chatContainerRef.current;
      if (!c) return;
      const nearTop = isNearTopForPaging();
      const viewingLatest = isViewingLatest();
      if (!showNotes && hasMore && !isFetching && nearTop && selectedEventId) {
        fetchMessages(selectedEventId, false, offset);
      }
      if (
        !showNotes &&
        hasNewerMessages &&
        !loadingNewerMessages &&
        viewingLatest
      ) {
        loadNewerMessages();
      }
      // §3.6: atLatest でも ANCHOR 窓(hasNewerMessages=true)の間は既読化しない。
      if (!showNotes && viewingLatest && !hasNewerMessages) {
        if (!(messages.length && isMyAdminMessage(messages[0]))) {
          markLatestRead();
        }
        setShowNewMsgBanner(false);
      }
    }, [
      fetchMessages,
      hasMore,
      hasNewerMessages,
      isFetching,
      isMyAdminMessage,
      isNearTopForPaging,
      isViewingLatest,
      loadNewerMessages,
      loadingNewerMessages,
      markLatestRead,
      messages,
      offset,
      selectedEventId,
      showNotes,
    ]);
    useEffect(() => {
      const c = chatContainerRef.current;
      if (c) {
        c.addEventListener('scroll', handleScroll);
        return () => c.removeEventListener('scroll', handleScroll);
      }
    }, [handleScroll]);

      /* ソケット：新着 / 既読更新 */
      useEffect(() => {
        // §3.3 RECONNECT: 再接続時に開いているイベントチャットを最新へ再同期(silent)。
        // handleNewEventMessage の stale フォールバックと同じ fetchNewMessagesRef 経由の
        // 差分取得を再利用する。チャットが開いていない/未選択なら何もしない。
        const handleConnect = () => {
          if (!isEventChatAreaOpenRef.current) return;
          if (!selectedEventIdRef.current) return;
          const fetchNewMessagesLatest = fetchNewMessagesRef.current;
          if (!fetchNewMessagesLatest) return;
          fetchNewMessagesLatest()
            .then(() => {
              lastDiffFetchRef.current = Date.now();
            })
            .catch(() => {});
        };

        const handleNewEventMessage = (payload = {}) => {
          const { eventId, threadId } = payload;
          if (!eventId && !threadId) return;
          refreshPendingMentionTargets();
          const currentSelected = selectedEventIdRef.current;
          const selectedThreadId =
            selectedThreadIdRef.current || resolveThreadIdByEvent(currentSelected);
        const incomingThreadId = threadId || resolveThreadIdByEvent(eventId);
        const sameThreadAsSelected = Boolean(
          currentSelected &&
            selectedThreadId &&
            incomingThreadId &&
            selectedThreadId === incomingThreadId
        );
        const targetEventId =
          (sameThreadAsSelected ? currentSelected : eventId) || currentSelected || eventId;
        const currentMessages = messagesRef.current;
        const isSearchModeNow = isSearchModeRef.current;
          const isSelectedChatOpen =
            isEventChatAreaOpenRef.current && (eventId === currentSelected || sameThreadAsSelected);
          if (targetEventId && isSelectedChatOpen) {
            const realtimeMessage = buildRealtimeEventTextMessage(payload, targetEventId);
            if (realtimeMessage) {
              const wasAtBottom = isViewingLatest();
              const alreadyKnown = messageIdsRef.current.has(realtimeMessage.id);
              if (!alreadyKnown) {
                messageIdsRef.current.add(realtimeMessage.id);
              }
              // 経路①: enriched payload の直接 merge。§3.3 SOCKET_MSG
              setMessages((prev) => mergeMessages(prev, [realtimeMessage]));
              if (!alreadyKnown) {
                setOffset((prev) => prev + 1);
              }
              lastDiffFetchRef.current = Date.now();

              const showNotesNow = showNotesRef.current;
              const unreadPatch = showNotesNow ? {} : { unreadCount: 0 };
              const sidebarBase = realtimeMessage.content || 'メッセージを送信しました';
              const sidebarPreview = sidebarBase.length > 50 ? `${sidebarBase.slice(0, 50)}…` : sidebarBase;
              const sidebarTime = nowJstDbString();
              if (isSearchModeNow) {
                setEvents(prev =>
                  prev.map(e =>
                    e.event_uuid === targetEventId
                      ? {
                          ...e,
                          last_message_preview: sidebarPreview,
                          last_message_time: sidebarTime,
                          last_message_format: realtimeMessage.message_format,
                          ...unreadPatch,
                        }
                      : e
                  )
                );
              } else if (orderFrozenRef.current) {
                setEvents(prev =>
                  prev.map(e =>
                    e.event_uuid === targetEventId
                      ? {
                          ...e,
                          last_message_preview: sidebarPreview,
                          last_message_time: sidebarTime,
                          last_message_format: realtimeMessage.message_format,
                          ...unreadPatch,
                        }
                      : e
                  )
                );
              } else {
                setEvents(prev =>
                  bumpEventToTop(prev, targetEventId, {
                    last_message_preview: sidebarPreview,
                    last_message_time: sidebarTime,
                    last_message_format: realtimeMessage.message_format,
                    ...unreadPatch,
                  })
                );
              }

              if (!showNotesNow && wasAtBottom) {
                selfReadGuardRef.current = Date.now();
                markEventUnreadLocally(targetEventId);
                postEventReadReceipt(targetEventId, realtimeMessage.id).catch(() => {});
                requestAnimationFrame(() => {
                  if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                  }
                });
              } else if (!showNotesNow && !isMyAdminMessage(realtimeMessage)) {
                const name = realtimeMessage.message_type === 'admin_message' ? '管理者' : 'メンバー';
                const bannerPreview = sidebarBase.length > 40 ? `${sidebarBase.slice(0, 40)}…` : sidebarBase;
                openBanner({ id: realtimeMessage.id, name, part: '', preview: bannerPreview, avatarUrl: null });
              }

              if (!isSearchModeRef.current) requestRefreshTop();
              refreshEventChatUnread();
              return;
            }
            const stale = Date.now() - lastDiffFetchRef.current > STALE_MS;
          if (stale || currentMessages.length === 0) {
            const fetchMessagesLatest = fetchMessagesRef.current;
            if (fetchMessagesLatest) {
              fetchMessagesLatest(targetEventId, true).then(() => {
                lastDiffFetchRef.current = Date.now();
              });
            }
            return;
          }
          const wasAtBottom = isViewingLatest();
          const fetchNewMessagesLatest = fetchNewMessagesRef.current;
          if (!fetchNewMessagesLatest) return;
          fetchNewMessagesLatest().then((added = []) => {
            lastDiffFetchRef.current = Date.now();
            if (selectedEventIdRef.current !== targetEventId) return;
            const currentMessagesAfter = messagesRef.current;
            // added は wire 順(ASC)の差分ページ、state は DESC — それぞれの newest を読む。
            const last = added.length ? added[added.length - 1] : currentMessagesAfter[0];
            const showNotesNow = showNotesRef.current;
            const unreadPatch = showNotesNow ? {} : { unreadCount: 0 };
            if (!showNotesNow && wasAtBottom && last) {
              markLatestRead();
            }

            if (added.length) {
              const lastAdded = added[added.length - 1];
              const isAdmin = lastAdded.message_type === 'admin_message';
              const isSystem = lastAdded.message_type === 'system_message';
              const excludeSenderMeta = shouldExcludeAdminSenderForMessage(lastAdded);
              const name = excludeSenderMeta
                ? 'システム'
                : (isSystem
                    ? 'システム'
                    : (isAdmin
                        ? formatAdminDisplayName({
                            adminDisplayName: lastAdded.admin_display_name || '',
                            fullName: lastAdded.admin_full_name || '',
                            username: lastAdded.admin_username || '',
                            role: lastAdded.admin_role || '',
                            isExternalAccount: lastAdded.admin_is_external_account === true,
                            externalAffiliationName: lastAdded.admin_external_affiliation_name || '',
                            useAsciiParentheses: true,
                          })
                        : (lastAdded.artist_name || 'メンバー')));

              const part = (!excludeSenderMeta && !isAdmin && !isSystem)
                ? (() => {
                    const ids = parsePartIds(lastAdded.artist_parts);
                    if (!ids.length) return '';
                    const main = ids[0];
                    const resolvePartLabel = findPartLabelByIdRef.current;
                    return resolvePartLabel ? resolvePartLabel(main) : '';
                  })()
                : '';

              const isEventCancelled =
                isSystem &&
                (lastAdded.message_format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED || lastAdded.message_format === 'event_cancelled');
              const isNote = isNoteMessage(lastAdded);
              const isNoteUpdate = isNoteUpdateMessage(lastAdded);
              const isNoteDelete = isNoteDeleteMessage(lastAdded);
              const noteBannerText =
                isNoteDelete ? '📝 ノート削除' :
                isNoteUpdate ? '📝 ノート更新' : '📝 ノート作成';
              const noteSidebarText =
                isNoteDelete ? 'ノートが削除されました。' :
                isNoteUpdate ? 'ノートが修正されました。' : 'ノートが作成されました。';
              const bannerText =
                isEventCancelled ? '🚫 イベント中止' :
                lastAdded.message_format === ADMIN_MESSAGE_FORMATS.FILE ? '📄 ファイル' :
                lastAdded.message_format === ADMIN_MESSAGE_FORMATS.IMAGE ? '🖼️ 画像' :
                isNote ? noteBannerText :
                (lastAdded.content || '');
              const bannerPreview = bannerText.length > 40 ? `${bannerText.slice(0, 40)}…` : bannerText;

              const rawPic = excludeSenderMeta
                ? null
                : (isAdmin ? lastAdded.admin_profile_picture : (!isSystem ? lastAdded.artist_profile_picture : null));
              const avatarUrl  = rawPic
                ? appendVariant(buildAssetUrl(isAdmin ? ADMIN_PROFILE_IMAGE_PATH : ARTIST_PROFILE_IMAGE_PATH, rawPic), 'proxy')
                : null;

              const sidebarBase =
                isEventCancelled ? 'イベントが中止となりました。' :
                lastAdded.message_format === ADMIN_MESSAGE_FORMATS.FILE ? 'ファイルが届いています。' :
                lastAdded.message_format === ADMIN_MESSAGE_FORMATS.IMAGE ? '画像が届いています。' :
                isNote ? noteSidebarText :
                (lastAdded.content || '');
              const sidebarPreview = sidebarBase.length > 50 ? `${sidebarBase.slice(0, 50)}…` : sidebarBase;

              if (isSearchModeNow) {
                setEvents(prev =>
                  prev.map(e =>
                    e.event_uuid === targetEventId
                      ? { ...e, last_message_preview: sidebarPreview, last_message_time: lastAdded.created_at || nowJstDbString(), ...unreadPatch }
                      : e
                  )
                );
              } else {
                if (orderFrozenRef.current) {
                  setEvents(prev => prev.map(e =>
                    e.event_uuid === targetEventId
                      ? { ...e, last_message_preview: sidebarPreview, last_message_time: lastAdded.created_at || nowJstDbString(), ...unreadPatch }
                      : e
                  ));
                } else {
                  setEvents(prev =>
                    bumpEventToTop(prev, targetEventId, {
                      last_message_preview: sidebarPreview,
                      last_message_time: lastAdded.created_at || nowJstDbString(),
                      ...unreadPatch
                    })
                  );
                }
              }

              if (!showNotesNow && !wasAtBottom) {
                openBanner({ id: lastAdded.id, name, part, preview: bannerPreview, avatarUrl });
              }
            }
          });

          if (isEventChatAreaOpenRef.current && selectedEventIdRef.current === targetEventId && !showNotesRef.current) {
            markEventUnreadLocally(targetEventId);
          }
        } else {
          clearPendingEventRead(targetEventId);
          if (isSearchModeNow) {
            fetchSidebarRow(eventId)
              .then(async (row) => {
                if (!row) return;
                if (row.performance_flag === 'additional' && row.use_existing_flyers && row.original_event_uuid && !row.flyer_front_url) {
                  const o = await getEventDetail(row.original_event_uuid);
                  if (o?.flyer_front_url) row.flyer_front_url = o.flyer_front_url;
                }
                setEvents(prev => prev.map(e =>
                  e.event_uuid === row.event_uuid
                    ? { ...e, last_message_preview: row.last_message_preview, last_message_time: row.last_message_time, last_message_format: row.last_message_format ?? e.last_message_format }
                    : e
                ));
              })
              .catch(() => {});
          } else {
            fetchSidebarRow(eventId)
              .then(async (row) => {
                if (row) {
                  if (row.performance_flag === 'additional' && row.use_existing_flyers && row.original_event_uuid && !row.flyer_front_url) {
                    const o = await getEventDetail(row.original_event_uuid);
                    if (o?.flyer_front_url) row.flyer_front_url = o.flyer_front_url;
                  }
                  upsertEventRow(row, { moveToTop: !orderFrozenRef.current });
                }
              })
              .finally(() => { requestRefreshTop(); });
          }
        }

        if (!isSearchModeRef.current) requestRefreshTop();
        refreshEventChatUnread();
      };

      const handleReadUpdated = (payload = {}) => {
        const { eventId, threadId, readMessageIds, read_message_ids: readMessageIdsSnake } = payload;
        const currentSelected = selectedEventIdRef.current;
        const selectedThreadId =
          selectedThreadIdRef.current || resolveThreadIdByEvent(currentSelected);
        const sameThreadAsSelected = Boolean(
          currentSelected &&
            selectedThreadId &&
            threadId &&
            selectedThreadId === threadId
        );
        if (eventId !== currentSelected && !sameThreadAsSelected) return;
        const ids = Array.isArray(readMessageIds) ? readMessageIds : readMessageIdsSnake;
        if (Array.isArray(ids) && ids.length > 0) {
          setMessages((prev) => patchMessageReadCountsByIds(prev, ids, payload, { scope: 'event' }));
          refreshEventChatUnread();
          return;
        }
        if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
        reconcileTimerRef.current = setTimeout(() => { reconcileRecentMessages(); }, 150);
        refreshEventChatUnread();
      };

      const handleEventMessageDeleted = ({ eventId, messageId, messageIds, threadId } = {}) => {
        const deletedIds = Array.isArray(messageIds) && messageIds.length
          ? messageIds.map((id) => String(id))
          : (messageId ? [String(messageId)] : []);
        if (!eventId || deletedIds.length === 0) return;
        const currentSelected = selectedEventIdRef.current;
        const selectedThreadId =
          selectedThreadIdRef.current || resolveThreadIdByEvent(currentSelected);
        const sameThreadAsSelected = Boolean(
          currentSelected &&
            selectedThreadId &&
            threadId &&
            selectedThreadId === threadId
        );
        const targetEventId =
          (sameThreadAsSelected ? currentSelected : eventId) || currentSelected || eventId;
        if (targetEventId === selectedEventIdRef.current) {
          setMessages((prev) => removeMessages(prev, deletedIds));
        }
        const isSearchModeNow = isSearchModeRef.current;
        fetchSidebarRow(targetEventId)
          .then(async (row) => {
            if (!row) return;
            if (row.performance_flag === 'additional' && row.use_existing_flyers && row.original_event_uuid && !row.flyer_front_url) {
              const o = await getEventDetail(row.original_event_uuid);
              if (o?.flyer_front_url) row.flyer_front_url = o.flyer_front_url;
            }
            if (isSearchModeNow) {
              setEvents((prev) =>
                prev.map((e) =>
                  e.event_uuid === row.event_uuid
                    ? { ...e, last_message_preview: row.last_message_preview, last_message_time: row.last_message_time, last_message_format: row.last_message_format ?? e.last_message_format }
                    : e
                )
              );
            } else {
              upsertEventRow(row, { moveToTop: false });
            }
          })
          .catch(() => {})
          .finally(() => {
            if (!isSearchModeRef.current) requestRefreshTop();
            refreshEventChatUnread();
          });
      };

      const handleTopologyChanged = ({ groupEventUuids = [] } = {}) => {
        const affected = Array.isArray(groupEventUuids) ? groupEventUuids : [];
        const currentSelected = selectedEventIdRef.current;
        const selectedAffected = currentSelected && affected.includes(currentSelected);
        if (!isSearchModeRef.current) requestRefreshTop();
        if (selectedAffected) {
          fetchSidebarRow(currentSelected)
            .then((row) => {
              if (!row) return;
              setSidebarDetails(row);
              upsertEventRow(row, { moveToTop: false });
            })
            .catch(() => {});
          const fetchMessagesLatest = fetchMessagesRef.current;
          if (fetchMessagesLatest) {
            fetchMessagesLatest(currentSelected, true).catch(() => {});
          }
          refreshAnnouncements({ silent: true }).catch(() => {});
        }
        refreshEventChatUnread();
      };

      const handleAnnouncementsUpdated = ({ eventId, threadId } = {}) => {
        const currentSelected = selectedEventIdRef.current;
        const selectedThreadId =
          selectedThreadIdRef.current || resolveThreadIdByEvent(currentSelected);
        const sameThreadAsSelected = Boolean(
          currentSelected &&
            selectedThreadId &&
            threadId &&
            selectedThreadId === threadId
        );
        if (String(eventId || '') !== String(currentSelected || '') && !sameThreadAsSelected) return;
        refreshAnnouncements({ silent: true }).catch(() => {});
      };

      const handleReactionUpdated = ({ eventId, threadId, messageId } = {}) => {
        const currentSelected = selectedEventIdRef.current;
        const selectedThreadId =
          selectedThreadIdRef.current || resolveThreadIdByEvent(currentSelected);
        const sameThreadAsSelected = Boolean(
          currentSelected &&
            selectedThreadId &&
            threadId &&
            selectedThreadId === threadId
        );
        if (!messageId) return;
        if (String(eventId || '') !== String(currentSelected || '') && !sameThreadAsSelected) return;
        refreshMessageReactionDetails(messageId).catch(() => {});
      };

      const handleLinkPreviewReady = makeLinkPreviewReadyHandler(setMessages);

      socket.on('connect', handleConnect);
      socket.on('new_event_message', handleNewEventMessage);
      socket.on('read_updated', handleReadUpdated);
      socket.on(SOCKET_EVENTS.EVENT_MESSAGE_DELETED, handleEventMessageDeleted);
      socket.on(SOCKET_EVENTS.EVENT_CHAT_ANNOUNCEMENTS_UPDATED, handleAnnouncementsUpdated);
      socket.on(SOCKET_EVENTS.EVENT_CHAT_MESSAGE_REACTION_UPDATED, handleReactionUpdated);
      socket.on(SOCKET_EVENTS.CHAT_LINK_PREVIEW_READY, handleLinkPreviewReady);
      socket.on('event_chat_topology_changed', handleTopologyChanged);
      return () => {
        socket.off('connect', handleConnect);
        socket.off('new_event_message', handleNewEventMessage);
        socket.off('read_updated', handleReadUpdated);
        socket.off(SOCKET_EVENTS.EVENT_MESSAGE_DELETED, handleEventMessageDeleted);
        socket.off(SOCKET_EVENTS.EVENT_CHAT_ANNOUNCEMENTS_UPDATED, handleAnnouncementsUpdated);
        socket.off(SOCKET_EVENTS.EVENT_CHAT_MESSAGE_REACTION_UPDATED, handleReactionUpdated);
        socket.off(SOCKET_EVENTS.CHAT_LINK_PREVIEW_READY, handleLinkPreviewReady);
        socket.off('event_chat_topology_changed', handleTopologyChanged);
      };
      }, [
        buildRealtimeEventTextMessage,
        isViewingLatest,
        openBanner,
        markLatestRead,
        reconcileRecentMessages,
        requestRefreshTop,
      fetchSidebarRow,
      getEventDetail,
      upsertEventRow,
      bumpEventToTop,
      refreshAnnouncements,
      refreshPendingMentionTargets,
      refreshMessageReactionDetails,
        resolveThreadIdByEvent,
        isMyAdminMessage,
        markEventUnreadLocally,
        postEventReadReceipt,
        clearPendingEventRead,
        refreshEventChatUnread
      ]);

      useEffect(() => {
        return () =>
          cleanupTimeoutRefs([
            bannerTimeoutRef,
            reconcileTimerRef,
            refreshTopTimerRef,
          ]);
      }, []);

    /* 既読送信（末尾が自分メッセージでない場合） */
    useEffect(() => {
      if (
        !shouldMarkLatestAsRead({
          selectedEventId,
          messagesLength: messages.length,
          showNotes,
          isViewingLatest,
        })
      ) return;
      markLatestRead();
    }, [selectedEventId, messages.length, showNotes, isViewingLatest, markLatestRead]);

    const shouldShowMessageSourceBadge = (message) => {
      return shouldShowEventMessageSourceBadge({
        message,
        isSelectedGroupUnified,
        selectedEventInfo,
        selectedEventId,
        resolveEventChatMode,
        resolveEventGroupCount,
        unifiedMode: CHAT_MODE_GROUP_UNIFIED,
      });
    };

    const resolveAvatarProfileTarget = useMemo(
      () =>
        createEventAvatarProfileTargetResolver({
          isAnnouncementMessage,
          participantSnapshot,
          adminLookup,
          participantArtistMap,
          buildAdminProfilePayload,
          buildArtistProfilePayload,
          buildParticipantEventMembershipLabel,
          buildParticipantEventPartSummary: (profileSource) =>
            buildParticipantEventPartSummary(profileSource),
          selectedEventId,
          effectiveGroupEvents,
          isUnifiedEventGroupChat: isSelectedGroupUnified,
          normalizeEventUuidList,
          pickFirstString,
        }),
      [
        adminLookup,
        participantSnapshot,
        participantArtistMap,
        buildAdminProfilePayload,
        buildArtistProfilePayload,
        buildParticipantEventMembershipLabel,
        buildParticipantEventPartSummary,
        isSelectedGroupUnified,
        selectedEventId,
        effectiveGroupEvents,
      ]
    );

    const handleAvatarClick = useCallback((message) => {
      const target = resolveAvatarProfileTarget(message);
      openProfileTarget(target);
    }, [openProfileTarget, resolveAvatarProfileTarget]);

    const {
      renderAnnouncementAvatarLeft,
      renderAdminAvatarLeft,
      renderArtistAvatarLeft,
    } = createEventAvatarRenderers({
      isNoteMessage,
      getAnnouncementToneClass,
      getAnnouncementIconClass,
      buildAssetUrl,
      appendVariant,
      adminImageRoot: ADMIN_PROFILE_IMAGE_PATH,
      artistImageRoot: ARTIST_PROFILE_IMAGE_PATH,
      personClassName: 'chat-avatar',
      clickableClassName: 'chat-avatar--clickable',
      initialClassName: 'chat-avatar-initial',
      announcementClassName: 'chat-avatar chat-avatar-announce',
    });

    /* 通知設定モーダル */
    const openSettingsModal = useCallback(
      async (eventInfo, { selectOnOpen = true } = {}) => {
        const baseInfo = eventInfo || selectedEventInfo || null;
        const eventId = baseInfo?.event_uuid || baseInfo?.eventId || selectedEventId;
        if (!eventId) return;
        if (selectOnOpen && eventId !== selectedEventId) {
          handleSelectEvent(eventId, { clearUrl: true });
        }
        setSettingsEvent(baseInfo);
        settingsEventIdRef.current = eventId;
        settingsEventRef.current = baseInfo;
        setShowSettingsModal(true);
        setIsLoadingSettings(true);
        try {
          const data = await getAdminEventChatNotifyMute(eventId);
          setMuteSetting(Boolean(data?.muted));
        } catch {
          setMuteSetting(true);
          toastError('通知設定の取得に失敗しました');
        } finally {
          setIsLoadingSettings(false);
        }
      },
      [handleSelectEvent, selectedEventId, selectedEventInfo, toastError],
    );
    const closeSettingsModal = () => {
      if (isSavingSettings) return;
      setShowSettingsModal(false);
      setSettingsEvent(null);
      settingsEventIdRef.current = null;
      settingsEventRef.current = null;
    };

    const clearSidebarLongPress = useCallback(() => {
      if (sidebarPressRef.current.timer) {
        clearTimeout(sidebarPressRef.current.timer);
        sidebarPressRef.current.timer = null;
      }
    }, []);

    const clearSidebarContextMenuBlock = useCallback(() => {
      if (sidebarPressRef.current.contextMenuTimer) {
        clearTimeout(sidebarPressRef.current.contextMenuTimer);
        sidebarPressRef.current.contextMenuTimer = null;
      }
      sidebarPressRef.current.suppressContextMenu = false;
    }, []);

    const blockSidebarContextMenu = useCallback(() => {
      clearSidebarContextMenuBlock();
      sidebarPressRef.current.suppressContextMenu = true;
      sidebarPressRef.current.contextMenuTimer = setTimeout(() => {
        sidebarPressRef.current.suppressContextMenu = false;
        sidebarPressRef.current.contextMenuTimer = null;
      }, 800);
    }, [clearSidebarContextMenuBlock]);

    useEffect(() => {
      const handleContextMenu = (event) => {
        if (!sidebarPressRef.current.suppressContextMenu) return;
        event.preventDefault();
        event.stopPropagation();
        clearSidebarContextMenuBlock();
      };
      window.addEventListener('contextmenu', handleContextMenu, true);
      return () => {
        window.removeEventListener('contextmenu', handleContextMenu, true);
      };
    }, [clearSidebarContextMenuBlock]);

    const handleSidebarTouchStart = useCallback(
      (eventInfo) => {
        if (!isNarrow) return;
        clearSidebarLongPress();
        sidebarPressRef.current.timer = setTimeout(() => {
          sidebarPressRef.current.timer = null;
          sidebarPressRef.current.suppressClick = true;
          blockSidebarContextMenu();
          openSettingsModal(eventInfo, { selectOnOpen: false });
        }, 1000);
      },
      [blockSidebarContextMenu, clearSidebarLongPress, isNarrow, openSettingsModal],
    );
    const toggleMuteImmediately = async (checked) => {
      const targetEventId = settingsEventIdRef.current || selectedEventId;
      if (!targetEventId || isSavingSettings) return;
      const prev = muteSetting;
      const eventIdAtStart = targetEventId;

      setMuteSetting(checked);
      setIsSavingSettings(true);
      try {
        await updateAdminEventChatNotifyMute({ eventId: eventIdAtStart, muted: !!checked });
        setEvents(prev =>
          prev.map(evt =>
            evt && evt.event_uuid === eventIdAtStart
              ? { ...evt, admin_muted: !!checked }
              : evt
          )
        );
        success(checked ? 'このイベントの通知をオフにしました' : 'このイベントの通知を再開しました');
      } catch {
        setMuteSetting(prev);
        toastError('通知設定の更新に失敗しました');
      } finally {
        setIsSavingSettings(false);
      }
    };

    /* 履歴遷移（ノート/画像/モバイル一覧 ←→ チャット） */
    useEffect(() => {
      const onPop = (ev) => {
        const st = ev.state;
        if (!st || st.scope !== 'admin-event-chat') return;
        if (st.view === 'list') {
          // モバイル：一覧全画面
          setShowNotes(false);
          setShowSettingsModal(false);
          mobileEventListPrefRef.current = true;
          mobileEventChatHistoryRef.current = false;
          setMobileView('list');
          return;
        }
        if (selectedEventId && st.eventId && st.eventId !== selectedEventId) return;

        switch (st.view) {
          case 'chat':
            setShowNotes(false);
            setShowSettingsModal(false);
            if (imgModalOpen) closeImgModal(true);
            window.dispatchEvent(new Event('admin-notes-close-image'));
            window.dispatchEvent(new Event('admin-notes-close-detail'));
            if (isNarrow) setMobileView('chat');
            break;
          case 'notes-list':
            setShowNotes(true);
            setShowSettingsModal(false);
            window.dispatchEvent(new Event('admin-notes-close-image'));
            window.dispatchEvent(new Event('admin-notes-close-detail'));
            if (isNarrow) setMobileView('chat');
            break;
          case 'notes-detail':
            setShowNotes(true);
            setShowSettingsModal(false);
            if (isNarrow) setMobileView('chat');
            break;
          case 'notes-image':
            setShowNotes(true);
            setShowSettingsModal(false);
            window.dispatchEvent(new Event('admin-notes-close-image'));
            if (isNarrow) setMobileView('chat');
            break;
          case 'chat-image':
            setShowSettingsModal(false);
            if (imgModalOpen) closeImgModal(true);
            if (isNarrow) setMobileView('chat');
            break;
          default:
            break;
        }
      };
      window.addEventListener('popstate', onPop);
      return () => window.removeEventListener('popstate', onPop);
    }, [selectedEventId, imgModalOpen, isNarrow]);

    /* D&D 送信用ヘルパ */
    const uploadSingleFile = useCallback(async (file) => {
      if (!file || !selectedEventId) return false;
      if (typeof file.size === 'number' && file.size > 100 * 1024 * 1024) return false;

      const fd = new FormData();
      fd.append('file', file);
      fd.append('senderType', 'admin');
      if (loggedInAdminId != null) fd.append('senderId', String(loggedInAdminId));
      if (replyDraft?.messageId) fd.append('replyToMessageId', String(replyDraft.messageId));

      try {
        await sendEventChatFileMessage(selectedEventId, fd);
        return true;
      } catch {
        return false;
      }
    }, [selectedEventId, loggedInAdminId, replyDraft]);

    const promptSelfAssignmentAfterSend = useCallback(async () => {
      if (!selectedEventId || !loggedInAdminId) return;
      const current = selectedEventInfo || events.find((event) => event?.event_uuid === selectedEventId);
      await confirmAssignmentForEvents({
        events: [current || { event_uuid: selectedEventId }],
        adminId: loggedInAdminId,
        message: 'あなたはこのイベントの担当者ですか？',
        onAssigned: (result) => {
          const assignments = result?.assignmentsByEventUuid?.[selectedEventId];
          if (Array.isArray(assignments)) {
            setSelectedEventInfo((prev) => (prev ? { ...prev, assigned_admins: assignments } : prev));
            setEvents((prev) =>
              prev.map((event) =>
                event?.event_uuid === selectedEventId ? { ...event, assigned_admins: assignments } : event
              )
            );
          }
          success('担当イベントに登録しました');
        },
      });
    }, [
      confirmAssignmentForEvents,
      events,
      loggedInAdminId,
      selectedEventId,
      selectedEventInfo,
      success,
    ]);

    const handleDropFiles = useCallback(async (files = [], { rejectedCount } = {}) => {
      if (!selectedEventId || showNotes) return;

      setIsUploading(true);
      try {
        let sentAny = false;
        let failed = 0;
        const plan = buildChatDropPlan(files, { rejectedCount });
        if (plan.notice) toastError(plan.notice);
        if (!plan.hasSendable) return;
        const images = plan.images;
        const others = plan.files;

        if (images.length > 0) {
          try {
            await sendImages({
              mode: 'admin',
              scope: 'event',
              eventId: selectedEventId,
              files: images,
              senderId: loggedInAdminId,
              replyToMessageId: replyDraft?.messageId || '',
            });
            sentAny = true;
          } catch {
            failed += images.length;
          }
        }

        for (const f of others) {
          // eslint-disable-next-line no-await-in-loop -- 送信順を保つため直列に送る
          const ok = await uploadSingleFile(f);
          if (ok) sentAny = true;
          else failed += 1;
        }
        if (sentAny) {
          const hasImage = images.length > 0;
          updateSidebarAfterSend(hasImage ? '画像が届いています。' : 'ファイルが届いています。');
          await fetchNewMessages();
          setReplyDraft(null);
          success(describeChatDropResult({ imageCount: images.length, fileCount: others.length }));
          await promptSelfAssignmentAfterSend();
        } else if (failed > 0) {
          toastError('ファイルの送信に失敗しました');
        }
      } finally {
        setIsUploading(false);
        requestAnimationFrame(() => {
          if (chatContainerRef.current) chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        });
      }
    }, [
      fetchNewMessages,
      loggedInAdminId,
      replyDraft?.messageId,
      selectedEventId,
      showNotes,
      success,
      toastError,
      promptSelfAssignmentAfterSend,
      updateSidebarAfterSend,
      uploadSingleFile,
    ]);

    const { isDragActive, overlayRect } = useChatDragDrop({
      areaRef: eventChatAreaRef,
      highlightRef: eventChatAreaRef,
      enabled: !!selectedEventId && !showNotes,
      onDrop: handleDropFiles,
      onInvalid: (msg) => console.warn(msg || 'この操作は無効です'),
      maxBytes: 100 * 1024 * 1024,
    });
  return {
    shouldShowMessageSourceBadge,
    handleAvatarClick,
    renderAnnouncementAvatarLeft,
    renderAdminAvatarLeft,
    renderArtistAvatarLeft,
    openSettingsModal,
    closeSettingsModal,
    clearSidebarContextMenuBlock,
    clearSidebarLongPress,
    handleSidebarTouchStart,
    toggleMuteImmediately,
    promptSelfAssignmentAfterSend,
    isDragActive,
    overlayRect,
  };
}
